import axios from 'axios';
import { refreshTokenDev, refreshTokenProd } from './login/login.api';
import { createBrowserRouter } from 'react-router-dom';
import { useAuthStore } from '../zustand-store/auth.store';
import { toast } from 'react-toastify';

const BASE_URL = import.meta.env.VITE_BASE_URL_BACKEND;

const router = createBrowserRouter(['/login']);
// Create an instance of axios
const axiosInstance = axios.create({
  baseURL: BASE_URL, // Replace with your API base URL
  timeout: 10000, // Request timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// A page fires several requests at once, and an expired session means all of
// them fail together. These two module-level values make sure that pile-up
// still produces exactly one refresh attempt and one redirect, not one per
// failed request.
let refreshPromise = null;
let sessionExpiryHandled = false;

const SESSION_EXPIRED_STORAGE_KEY = 'sessionExpiredNotice';

// 401 bodies the backend sends when the access token has expired and a refresh should be tried.
// 'Token expired' is the pre-session wording, kept for environments still on the older build.
const ACCESS_TOKEN_EXPIRED_MESSAGES = ['Access Token Expired', 'Token expired'];
// 401 bodies meaning the session itself is gone (revoked, expired, refresh token reused):
// refreshing cannot help, the user has to log in again.
const SESSION_DEAD_MESSAGES = ['Session Expired', 'Session not found', 'Invalid refresh token'];

// AccessTokenMiddlware (backend) used to send its 401s as a raw text body
// instead of the app's usual { message: '...' } JSON shape, which meant this
// exact check could never match. It now sends JSON too, but keep this
// tolerant of both in case an environment is still running the older build.
function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  return data?.message;
}

// Ends the local session and hard-navigates to /login with a single toast. Used when refresh
// fails, and when the backend pushes SESSION_INVOKED for this session.
export function redirectToLoginOnSessionExpiry(
  message = 'Your session has expired. Please log in again.'
) {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  useAuthStore.getState().clearAuth();
  // dev-mode refresh token, so a reload can't silently log back in
  localStorage.removeItem('token');
  try {
    // the login page shows this, since the toast is lost in the full-page redirect
    sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, message);
  } catch (e) {
    /* no-op */
  }
  toast.error(message, {
    toastId: 'session-expired'
  });
  window.location.href = '/login';
}

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // You can add headers here, for example, adding an authorization token
    const token = useAuthStore.getState().accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log(error, '=error====');

    // Any status codes that falls outside the range of 2xx causes this function to trigger
    if (error.response) {
      const message = extractErrorMessage(error);
      const status = error.response.status;

      if (status === 401 && SESSION_DEAD_MESSAGES.includes(message)) {
        redirectToLoginOnSessionExpiry();
        // see the comment on the same return below
        return new Promise(() => {});
      }

      const accessTokenExpired = status === 401 && ACCESS_TOKEN_EXPIRED_MESSAGES.includes(message);

      if (!accessTokenExpired) {
        if (Array.isArray(message)) {
          // If the message is an array, display each message as a separate toast
          message.forEach((msg) => {
            if (typeof msg === 'string') {
              toast.error(msg);
            }
          });
        } else if (typeof message === 'string') {
          if (
            !/^Too many failed login attempts\. Account locked\. Please try After \d+ mins$/.test(
              message
            )
          ) {
            toast.error(message);
          }
        } else {
          // Fallback for unknown error formats
          toast.error('Something went wrong');
        }
        // Show the toast, but still reject - returning a plain value here
        // resolves the *original* request's promise with that value instead
        // of the expected { data: { data: ... } } shape, so every caller
        // that reads response?.data?.data silently gets undefined and pages
        // downstream (e.g. table/list components) can crash on it.
        return Promise.reject(error);
      }

      // Access token expired: refresh once, then retry the original request once.
      const originalRequest = error.config;
      if (!originalRequest._retried) {
        originalRequest._retried = true;
        // Several requests can hit this at once (a page fires them in
        // parallel) - share one refresh attempt instead of each request
        // racing its own. The refresh token rotates on every use, so a
        // second concurrent refresh would replay a stale token and the
        // backend would revoke the whole session.
        if (!refreshPromise) {
          refreshPromise = (
            import.meta.env.VITE_APP_ENV === 'dev' ? refreshTokenDev() : refreshTokenProd()
          ).finally(() => {
            refreshPromise = null;
          });
        }

        const newAccessToken = await refreshPromise;
        if (newAccessToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
        console.log('Refresh returned no access token - logging out');
      }

      // Refresh failed ('Session Expired' etc.), or the retried request was
      // rejected again - the session is dead.
      redirectToLoginOnSessionExpiry();
      // We're about to hard-navigate to /login (full page reload), so
      // every component on the current page is moments from being torn
      // down anyway. Different endpoints nest their payload at different
      // depths (some callers read response.data.data, others
      // response.data.data.data), so there's no single fake "empty"
      // shape that's safe for all of them - rejecting would also make
      // every in-flight page fire its own "Error fetching X" toast on
      // top of the one clear message above. Returning a promise that
      // never settles avoids both: nothing else runs before the redirect.
      return new Promise(() => {});
    } else if (error.request) {
      // The request was made but no response was received
      console.log('No response received');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
